import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  CleaningControlAction,
  UseCleaningControlsStateProps,
  UseCleaningControlsStateResult,
} from '../types/CleaningControls.types';
import { COMMAND_TYPE_TO_CONTROL_ACTION } from '../utils/constants';
import { selectCustomerId } from 'app/cross-cutting-concerns/authentication/state/authenticationSelectors';
import { RobotCleaningControlActions } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningControl/robotCleaningControlSlice';
import * as robotCleaningControlSelectors from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningControl/robotCleaningControlSelectors';
import * as cleaningControlConstants from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningControl/utils/constants';
import {
  selectRoutineLastRouteName,
  selectRoutineStoppedStep,
  // eslint-disable-next-line max-len
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/routineTimeline/routineTimelineSelectors';
import type { IState } from 'app/cross-cutting-concerns/state-management/interfaces/IState';
import {
  hasRunEnded,
  hasRunStoppedEarly,
  // eslint-disable-next-line max-len
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningProgress/utils/taskStatus';
import { useRobotCleaningProgressEntry } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningProgress/hooks/useRobotCleaningProgressEntry';

export const useCleaningControlsState = ({
  robotId,
  materialNumber,
  serialNumber,
  model,
  isRobotCleaning,
}: UseCleaningControlsStateProps): UseCleaningControlsStateResult => {
  const dispatch = useDispatch();
  // The buttons stay loading until the robot's task shows a command's result, so the task must be followed
  // whenever the controls are shown, not only while the live map is open.
  const task = useRobotCleaningProgressEntry({ id: robotId, materialNumber, serialNumber }, true);
  const customerId = useSelector(selectCustomerId);
  const isPaused = useSelector(robotCleaningControlSelectors.selectRobotCleaningControlIsPaused);
  const isPausedResolved = useSelector(robotCleaningControlSelectors.selectRobotCleaningControlIsPausedResolved);
  const pendingCommandType = useSelector(robotCleaningControlSelectors.selectRobotCleaningControlPendingCommandType);
  const lastRouteSkippedTaskId = useSelector(
    robotCleaningControlSelectors.selectRobotCleaningControlLastRouteSkippedTaskId
  );
  const lastRouteName = useSelector(selectRoutineLastRouteName);
  const stoppedStep = useSelector((state: IState) => selectRoutineStoppedStep(state, task?.deviceTaskId));

  // Locked while the task is still the one whose last route was skipped (or its data is gone); a new task unlocks it.
  const isCancelledBySkip =
    !!lastRouteSkippedTaskId && (task?.deviceTaskId ?? lastRouteSkippedTaskId) === lastRouteSkippedTaskId;

  const pendingAction: CleaningControlAction | null = pendingCommandType
    ? COMMAND_TYPE_TO_CONTROL_ACTION[pendingCommandType] ?? null
    : null;

  useEffect(() => {
    if (customerId) {
      dispatch(RobotCleaningControlActions.subscribeToPausedState({ model, customerId, materialNumber, serialNumber }));
    }

    return (): void => {
      dispatch(RobotCleaningControlActions.unsubscribeFromPausedState());
    };
  }, [dispatch, model, customerId, materialNumber, serialNumber]);

  // Reset only when the robot changes or the controls unmount. Doing it in the effect above would
  // also reset on customerId/model changes for the same robot, clearing the pending command mid-flight.
  useEffect(
    () => (): void => {
      dispatch(RobotCleaningControlActions.sendCleaningCommandReset());
    },
    [dispatch, materialNumber, serialNumber]
  );

  const sendCommand = (action: cleaningControlConstants.RobotCleaningControlAction): void => {
    dispatch(
      RobotCleaningControlActions.sendCleaningCommandRequest({
        commandType: cleaningControlConstants.CLEANING_CONTROL_ACTION_TO_COMMAND_TYPE[action],
        materialNumber,
        serialNumber,
        model,
        robotId,
      })
    );
  };

  return {
    isPaused,
    isRoutineRun: (task?.stepCount ?? 0) > 0,
    hasTaskEnded:
      hasRunEnded(
        task?.taskStatus,
        task?.step,
        task?.stepCount,
        task?.name,
        lastRouteName,
        isRobotCleaning,
        stoppedStep
      ) ||
      hasRunStoppedEarly(
        task?.taskStatus,
        task?.step,
        task?.stepCount,
        task?.name,
        lastRouteName,
        isRobotCleaning,
        stoppedStep
      ),
    // The robot ignores these while it is paused; the CV50 accepts them.
    isBlockedWhilePaused: model === 'B50' && isPaused,
    pendingAction,
    isRequestPending: pendingCommandType !== null,
    // Locked until the paused state is known, so Pause/Resume can't act on a guessed state.
    isDisabled: pendingAction !== null || !isPausedResolved || isCancelledBySkip,
    handleSendHome: (): void => sendCommand(cleaningControlConstants.RobotCleaningControlAction.SendHome),
    handlePauseOrResume: (): void =>
      sendCommand(
        isPaused
          ? cleaningControlConstants.RobotCleaningControlAction.Resume
          : cleaningControlConstants.RobotCleaningControlAction.Pause
      ),
    handleCancelCleaning: (): void => sendCommand(cleaningControlConstants.RobotCleaningControlAction.CancelCleaning),
    handleSkipRoute: (): void => sendCommand(cleaningControlConstants.RobotCleaningControlAction.SkipRoute),
  };
};
